import type { GiverState, SeekerState } from '../../types';
import type { CallCapView } from './callCap';

/**
 * THE FLOATING QUEUE PILL'S ARITHMETIC AND ITS WORDS — the two things about it
 * a test can hold, said where they are said once.
 *
 * David, 2026-09-14, after the pill covered the call's top bar on a real
 * phone: "While the call stage is up the pill is not rendered at all. Away
 * from the stage it shows the in-call state with two verbs. While queued it
 * shows the wait state with Leave queue. No spinner without a verb: every
 * state names its action."
 *
 * Both halves are pure functions rather than JSX for the reason `callLayout`
 * exists: jsdom has no layout, so a size expressed only in a class name is a
 * size no test can see, and an accessible name built inline in a ternary is a
 * name that quietly stops matching the state it describes.
 */

/** The compact pill — `w-16 h-16`. */
export const PILL_COMPACT_PX = 64;

/**
 * The expanded panel. It is a MAX height and the panel carries it as an inline
 * style, so the number this file states is the number the browser enforces —
 * a panel that grows past it scrolls inside itself instead of walking off the
 * bottom of a 640px phone.
 */
export const PILL_PANEL_WIDTH_PX = 320;
export const PILL_PANEL_MAX_HEIGHT_PX = 320;

/** The gap kept between the panel and the edges of the screen. */
export const PILL_VIEWPORT_MARGIN_PX = 8;

export interface PillViewport {
  widthPx: number;
  heightPx: number;
}

/**
 * Does the expanded panel fit on this screen with its margins?
 *
 * The smallest phone this app is judged at is 360×640, which is where the
 * expanded card has to be checked: a panel sized for 800 is a panel that puts
 * its own verbs under the fold on the shorter screen, and "tap for options"
 * with no reachable options is worse than a spinner.
 */
export function pillPanelFits({ widthPx, heightPx }: PillViewport): boolean {
  return (
    PILL_PANEL_WIDTH_PX + 2 * PILL_VIEWPORT_MARGIN_PX <= widthPx &&
    PILL_PANEL_MAX_HEIGHT_PX + 2 * PILL_VIEWPORT_MARGIN_PX <= heightPx
  );
}

/**
 * WHAT THE PILL IS SHOWING.
 *
 * `hidden` is a state and not an absence: the overlay is mounted by the
 * dashboard layout on every page, so "there is nothing to say" has to be one
 * of the answers this function gives.
 */
export type QueuePillKind = 'hidden' | 'in-call' | 'queued' | 'cooldown';

export interface QueuePillFacts {
  mode: 'giver' | 'seeker';
  giverState?: GiverState;
  seekerState?: SeekerState;
  queuePosition?: number | null;
  /**
   * WHAT THE CALL'S CLOCK SAYS — the SAME view the stage's top bar is handed,
   * not a second reading of the same anchor.
   *
   * The pill used to take `elapsedSeconds` and format it itself, which was
   * correct for eighty-five minutes of a ninety-minute call and wrong for the
   * last five: the stage had started counting DOWN and was saying that the call
   * was wrapping up, while the pill went on counting up. One call, two numbers,
   * disagreeing about the direction of time.
   *
   * `callCapView` owns that decision — the four phases, the string each one
   * shows, and where the thresholds are — so both surfaces ask it rather than
   * each holding an opinion (class C). Null when there is no session to clock,
   * which is a state and not a zero.
   */
  cap?: CallCapView | null;
  /**
   * IS THERE A CALL TO GO BACK TO — `isCallSessionLive`, and nothing else.
   *
   * The queue state alone cannot answer it. `giverState` still reads `ACTIVE`
   * while the feedback screen is up, so a pill that trusted it sat on top of
   * that screen offering to return to a call that had ended (David, on a
   * phone, 2026-09-14). The one predicate lives in the empathy-queue store
   * beside the states it reads; this file takes its answer rather than keeping
   * a second copy of the list of endings (class D).
   */
  callLive: boolean;
}

export function queuePillKind({
  mode,
  giverState,
  seekerState,
  callLive,
}: QueuePillFacts): QueuePillKind {
  if (mode === 'giver') {
    if (!giverState || giverState === 'OFFLINE') return 'hidden';
    if (giverState === 'COOLDOWN') return 'cooldown';
    // The queue says ACTIVE, and only the predicate knows whether that is
    // still a call or the feedback screen standing on the ashes of one.
    if (giverState === 'ACTIVE') return callLive ? 'in-call' : 'hidden';
    return 'queued';
  }
  if (!seekerState) return 'hidden';
  if (seekerState === 'CONNECTED') return callLive ? 'in-call' : 'hidden';
  return 'queued';
}

/**
 * The line under the wait — what is actually happening, in the words the panel
 * and the accessible name BOTH use. One table, because a pill whose label says
 * "position 2" while its panel says "finding a giver" is two descriptions of
 * one fact.
 */
export function queuePillStatus(facts: QueuePillFacts): string {
  const { mode, giverState, seekerState, queuePosition } = facts;
  if (mode === 'giver') {
    if (giverState === 'PINGED') return 'ready to be matched';
    if (queuePosition) return `position ${queuePosition}`;
    return 'waiting for someone who needs support';
  }
  if (seekerState === 'FALLBACK_ACTIVITY') return 'no givers free yet';
  // P7: a group request while the group has it.
  if (seekerState === 'WAITING') return 'asking your group';
  return 'finding a giver';
}

/**
 * THE ACCESSIBLE NAME — the state, and the fact that tapping opens the verbs.
 *
 * "In a call, 3:12, tap for options" rather than "Expand seeker queue status":
 * the old name described the WIDGET, so a screen-reader user who wanted to
 * know whether they were still in a call had to open it to find out.
 */
export function queuePillName(facts: QueuePillFacts): string {
  const kind = queuePillKind(facts);
  if (kind === 'hidden') return '';
  if (kind === 'cooldown') return 'Taking a break, tap for options';
  if (kind === 'in-call') {
    const cap = facts.cap;
    return cap ? `In a call, ${cap.clockText}, tap for options` : 'In a call, tap for options';
  }
  // A7·3: while queued, the tap goes back to the waiting screen.
  return `In the queue, ${queuePillStatus(facts)}, tap to open the waiting screen`;
}
